import { useState } from "react";
import { CaretUpDown, Check, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const SearchSelect = ({
  value,
  options = [],
  onChange,
  onAddNew,
  addNewLabel = "Add new",
  allowCustom = false,
  tabbed = false,
  placeholder = "Select",
  searchPlaceholder = "Search…",
  emptyText = "Nothing found",
  testId,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("All");
  const current =
    options.find((o) => o.value === value) ||
    (value ? { value, label: value } : null);
  const typed = query.trim();
  const showCustom =
    allowCustom && typed && !options.some((o) => o.value === typed);
  const hints = [];
  options.forEach((o) => {
    const h = o.hint || "";
    if (h && !hints.includes(h)) hints.push(h);
  });
  const tabs = tabbed
    ? ["All", ...hints, ...(allowCustom ? ["Custom"] : [])]
    : [];
  const visibleOptions =
    !tabbed || tab === "All"
      ? options
      : tab === "Custom"
        ? []
        : options.filter((o) => (o.hint || "") === tab);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testId}
          className={`h-12 w-full justify-between border-zinc-800 bg-zinc-950 px-4 text-left text-sm font-normal hover:border-indigo-400/60 hover:bg-zinc-900 ${className}`}
        >
          <span className="min-w-0 truncate">
            {current ? (
              <>
                {current.label}
                {current.hint && (
                  <span className="ml-2 text-[11px] uppercase tracking-wider text-indigo-300">
                    {current.hint}
                  </span>
                )}
              </>
            ) : (
              <span className="text-zinc-500">{placeholder}</span>
            )}
          </span>
          <CaretUpDown size={15} weight="bold" className="ml-2 text-zinc-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={`${tabbed ? "w-[min(26rem,92vw)]" : "w-[min(22rem,90vw)]"} border-zinc-800 bg-zinc-900 p-0`}
        data-testid={testId ? `${testId}-panel` : undefined}
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            data-testid={testId ? `${testId}-search` : undefined}
            className="h-11"
          />
          {tabbed && tabs.length > 1 && (
            <div
              className="flex flex-wrap gap-1 border-b border-zinc-800 px-2 py-2"
              data-testid={testId ? `${testId}-tabs` : undefined}
            >
              {tabs.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={testId ? `${testId}-tab-${t.toLowerCase()}` : undefined}
                  onClick={() => setTab(t)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors duration-150 ${
                    tab === t
                      ? "bg-indigo-500 text-white"
                      : "border border-zinc-800 text-zinc-400 hover:text-indigo-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
          <CommandList>
            <CommandEmpty className="px-3 py-4 text-xs text-zinc-500">
              {tabbed && tab === "Custom"
                ? "Type a name above, then pick “Use …” to add a custom line"
                : emptyText}
            </CommandEmpty>
            <CommandGroup>
              {showCustom && (
                <CommandItem
                  value={typed}
                  data-testid={testId ? `${testId}-use-typed` : undefined}
                  onSelect={() => {
                    onChange(typed);
                    setQuery("");
                    setOpen(false);
                  }}
                  className="cursor-pointer gap-2 text-sm text-indigo-300 aria-selected:bg-indigo-500/15"
                >
                  <Plus size={14} weight="bold" />
                  <span className="min-w-0 flex-1 truncate">Use “{typed}”</span>
                </CommandItem>
              )}
              {visibleOptions.map((o) => (
                <CommandItem
                  key={o.value || "blank"}
                  value={`${o.label} ${o.hint || ""}`}
                  data-testid={
                    testId
                      ? `${testId}-option-${o.value || "blank"}`
                      : undefined
                  }
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className="cursor-pointer gap-2 text-sm aria-selected:bg-indigo-500/15"
                >
                  <Check
                    size={14}
                    weight="bold"
                    className={
                      o.value === value ? "text-indigo-400" : "opacity-0"
                    }
                  />
                  {o.img ? (
                    <img
                      src={o.img}
                      alt=""
                      className="h-7 w-7 shrink-0 rounded border border-zinc-700 object-cover"
                    />
                  ) : null}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.hint && (
                    <span className="shrink-0 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-300">
                      {o.hint}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {onAddNew && (
            <button
              type="button"
              data-testid={testId ? `${testId}-add-new` : undefined}
              onClick={() => {
                setOpen(false);
                onAddNew();
              }}
              className="flex w-full items-center gap-2 border-t border-zinc-800 px-3 py-3 text-left text-xs font-bold uppercase tracking-widest text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/10"
            >
              <Plus size={14} weight="bold" /> {addNewLabel}
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchSelect;
